import { useUpdateEffect } from '@cian/react-utils';

import { equals } from 'ramda';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useMapContext } from '@/microfrontends/map/shared/context/MapContext';

import { type TJsonQuery } from '@/packages/JsonQuery';
import { useGetMapConfigForMapQuery } from '@/packages/query-state/shared/store/search-offers-index/v1/get-config-for-map/query';
import { useGetClustersQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/query';
import { useFiltersSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { useListingLayoutIsNarrowSelector } from '@/packages/redux-state/store/slices/listingLayout';
import {
  mapConfigurationActions,
  useMapConfigurationSelector,
} from '@/packages/redux-state/store/slices/mapConfiguration';

export function useMapUpdateOnFiltersChange(): void {
  const dispatch = useDispatch();
  const { map } = useMapContext();
  const { initialized } = useMapConfigurationSelector();
  const isNarrowListing = useListingLayoutIsNarrowSelector();

  const { refetch: refetchMapConfiguration } = useGetMapConfigForMapQuery({ enabled: initialized && isNarrowListing });
  const { refetch: refetchMapClusters } = useGetClustersQuery({ enabled: initialized && isNarrowListing });

  const filters = useFiltersSelector();
  const prevFilters = useRef<TJsonQuery>(filters);

  const handleRefreshMapConfig = useCallback(async () => {
    const res = await refetchMapConfiguration();

    if (res.data) {
      dispatch(
        mapConfigurationActions.setMapConfiguration({
          center: [res.data.center.lat, res.data.center.lng],
          zoom: res.data.zoom,
          bounds: map.getBounds(),
        }),
      );

      map.setCenter([res.data.center.lat, res.data.center.lng], res.data.zoom);
    }
  }, [dispatch, map, refetchMapConfiguration]);

  useUpdateEffect(() => {
    if (isNarrowListing) {
      if (hasGeoChanged(prevFilters.current, filters)) {
        handleRefreshMapConfig();
      } else {
        refetchMapClusters();
      }
    }
  }, [filters, isNarrowListing]);
}

function hasGeoChanged(prevFilters: TJsonQuery, newFilters: TJsonQuery): boolean {
  return (
    !equals(prevFilters.geo?.value, newFilters.geo?.value) ||
    !equals(prevFilters.region?.value, newFilters.region?.value)
  );
}
